import { patchBookingForSeatSelection } from "../../../../app/api/patchBookingForSeatSelection";
import { getBooking } from "../../../../app/api/getBooking";
import { BaseAction } from "../../../../core/actions/BaseAction";
import { getRecoilStateValue, updateRecoilState } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { ConfirmationSlugEnum, TrainTypeEnum } from "../../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { ISelectedTrainLegs } from "../../../../Orchestrator/Types/Index";
import { getPlaceMapsAction } from "../../../LogicActions/Checkout/Seating/GetPlaceMapsAction";
import { prepareBookingSummaryAction } from "../../../LogicActions/Checkout/Summary/PrepareBookingSummaryAction";
import { mergeUpgradePaymentSummary } from "../../../LogicActions/Checkout/Summary/mergeUpgradePaymentSummary";
import { showLoader, hideLoader } from "../../../../Lib/Utils/validationUtil";
import { storeBookingResponseAtom } from "../../../../Lib/Utils/tripDirectionRecovery";
import { showNotification } from "../../../../Services/notificationService";

export class ConfirmSeatSelectionAction extends BaseAction<{
    result: Record<string, string> | null
}
    ,
    {
        seats: Record<string, string>[],
        requestBody: any,
        bookingId: string,
        segmentIndex: number,
        intl: any,
        isManageSeating?: boolean
    }> {

    private static processingPassengers = new Set<string>();
    private static currentPromise: Promise<any> = Promise.resolve();
    private static queue: Array<() => Promise<any>> = [];
    private static isProcessing = false;
    private static processedPayloads = new Set<string>();
    private static lastBookingId = "";

    /**
     * Clear dedup entries for a given booking so that legitimate re-selections
     * after navigating away are not silently dropped.
     * Call this when the active booking changes (e.g. in useEffect on bookingId).
     */
    static clearDedup(bookingId?: string): void {
        if (bookingId) {
            const prefix = `${bookingId}::`;
            [...this.processedPayloads]
                .filter((k) => k.startsWith(prefix))
                .forEach((k) => this.processedPayloads.delete(k));
        } else {
            this.processedPayloads.clear();
            this.lastBookingId = "";
        }
    }

    private static async processQueue() {
        if (this.isProcessing || this.queue.length === 0) return;

        this.isProcessing = true;
        while (this.queue.length > 0) {
            const nextOperation = this.queue[0];
            try {
                await nextOperation();
            } catch (error) {
                console.error("Error processing queue operation:", error);
            }
            this.queue.shift();
        }
        this.isProcessing = false;
    }

    override async run(): Promise<{ result: Record<string, string> | null }> {
        let seatsData: Record<string, string> = {};

        // Extract passenger ID from requestBody
        const passengerId = this.params?.requestBody?.placeSelections?.[0]?.places?.[0]?.passengerRef;

        // If no passenger ID found or this passenger is already being processed, return early
        if (!passengerId || ConfirmSeatSelectionAction.processingPassengers.has(passengerId)) {
            return { result: null };
        }

        try {
            ConfirmSeatSelectionAction.processingPassengers.add(passengerId);

            const { seats, requestBody, bookingId, segmentIndex, isManageSeating, intl } = this.params!;

            // Auto-clear stale dedup entries when the active booking changes so
            // legitimate seat re-selections on a new booking are never silently dropped.
            if (bookingId !== ConfirmSeatSelectionAction.lastBookingId) {
                ConfirmSeatSelectionAction.clearDedup(ConfirmSeatSelectionAction.lastBookingId);
                ConfirmSeatSelectionAction.lastBookingId = bookingId;
            }

            // Snapshot isUpgrade synchronously before any await so that
            // concurrent atom resets (e.g. UnmountPassengerDetailAction) cannot
            // change the branch taken inside the queued async operation .
            const isUpgrade = getRecoilStateValue("isUpgradeToPremiumStateAtom") as boolean;
            // Snapshot quick-exchange too: a seat change inside a quick-exchange
            // checkout must rebuild the summary via the quick-exchange path so the
            // QE total (admission delta + fees) applies. Falling back to the
            // manage-addons path bills the full ticket + addons again.
            const isQuickExchange = getRecoilStateValue("isQuickExchangeStateAtom") as boolean;

            // Scope the fingerprint to the current bookingId so the same seat
            // selection on a DIFFERENT booking is never silently dropped (cross-
            // booking dedup regression). Within the same booking the dedup still
            // prevents double-submit on rapid re-clicks.
            const payloadTrack = `${bookingId}::${JSON.stringify(requestBody)}`;
            if (ConfirmSeatSelectionAction.processedPayloads.has(payloadTrack)) {
                ConfirmSeatSelectionAction.processingPassengers.delete(passengerId);
                return { result: null };
            }
            ConfirmSeatSelectionAction.processedPayloads.add(payloadTrack);

            const updatedSelectedSeats = {
                ...seats,
                [segmentIndex]: seats[segmentIndex]
            };

            // updateRecoilState("selectedSeatsAtom", seats[segmentIndex]);
            updateRecoilState("selectedSeatsAtom", updatedSelectedSeats);
            updateRecoilState("confirmedSeatsAtom", updatedSelectedSeats);
            seatsData = seats[segmentIndex];

            // Create a new operation for this passenger.
            // Snapshot selectedTrainLegs BEFORE any await — same race rule as
            // ApplyCheckoutUpsellAction: a sibling flow could rewrite the atom
            // via updateLegTripSummary during the network window, leaving the
            // recovery keyed off post-flip state.
            const priorSelectedTrainLegs = getRecoilStateValue<ISelectedTrainLegs>("selectedTrainLegsAtom");
            const operation = async () => {
                // Show loader at the start of the entire operation
                showLoader();

                try {
                    const response: any = await patchBookingForSeatSelection(bookingId, requestBody, false);
                    if (response.status === 'ok' && response.answer) {
                        getPlaceMapsAction.setParams({ booking: response.answer, trainType: TrainTypeEnum.outbound, isManageSeating: isManageSeating });
                        await getPlaceMapsAction.execute();

                        if (!isManageSeating) {
                            const freshBooking = await getBooking(bookingId, false);
                            if (freshBooking.status === 'ok' && freshBooking.answer?.booking) {
                                // Recover gateway direction-flip before any
                                // downstream consumer reads booking.trips
                                // from the atom or via setParams. See
                                // tripDirectionRecovery.ts for the gateway
                                // bug this guards against.
                                const recoveredResponse = storeBookingResponseAtom(freshBooking.answer, priorSelectedTrainLegs) ?? freshBooking.answer;
                                if (isUpgrade) {
                                    // Patch addon/seat data directly into the existing upgrade
                                    // summary, preserving fare delta, exchange fees intact.
                                    mergeUpgradePaymentSummary(recoveredResponse.booking);
                                } else if (isQuickExchange) {
                                    // Quick-exchange seat change: rebuild via the
                                    // quick-exchange path (not manage-addons) so the QE
                                    // total formula applies (admission delta + fees;
                                    // carried-over addons stay already-paid). Pass the
                                    // already-paid addon credit from the atom so the
                                    // summary matches the initial quick-exchange render.
                                    prepareBookingSummaryAction.setParams({
                                        ...recoveredResponse.booking,
                                        isFrom: ConfirmationSlugEnum.quick_exchange,
                                        changeTripAlreadyPaidAddons: getRecoilStateValue("changeTripAlreadyPaidAddonsAtom") || {},
                                    } as any);
                                    await prepareBookingSummaryAction.execute();
                                } else {
                                    prepareBookingSummaryAction.setParams({ ...recoveredResponse.booking, isManageAddons: true } as any);
                                    await prepareBookingSummaryAction.execute();
                                }
                            } else {
                                showNotification("error", intl?.formatMessage({ id: "failedToRefreshBooking" }), "");
                            }
                        }
                    } else {
                        updateRecoilState("selectedSeatsAtom", []);
                        updateRecoilState("confirmedSeatsAtom", []);
                        showNotification("error", intl?.formatMessage({ id: "failedToChangeSeat" }), "");
                    }
                    return response;
                } finally {
                    // Hide loader at the end of the entire operation
                    hideLoader();
                }
            };

            // Add the operation to the queue
            ConfirmSeatSelectionAction.queue.push(operation);

            // Start processing the queue if not already processing
            if (!ConfirmSeatSelectionAction.isProcessing) {
                await ConfirmSeatSelectionAction.processQueue();
            }

            updateRecoilState("seatsChangedOnCheckoutAtom", true);

        } catch (error) {
            console.error("Error in ConfirmSeatSelectionAction:", error);
        } finally {
            if (passengerId) {
                ConfirmSeatSelectionAction.processingPassengers.delete(passengerId);
            }
            return { result: seatsData };
        }
    }
}

// Create a singleton instance
export const confirmSeatSelectionAction = new ConfirmSeatSelectionAction(); 